import React, { useState, useMemo } from 'react';
import { Check, Loader2, CheckCircle, Combine, ExternalLink, Copy, ArrowRight, XCircle, Eye, Download, FileSignature, RotateCcw } from 'lucide-react';
import type { PensionFund } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import { pensionFundsApi } from '../../services/api';
import PensionFundCommission from './PensionFundCommission';

export interface PensionFundStatusFormProps {
  pensionFund: PensionFund;
  statusColors: { bg: string; light: string };
  selectedStatus: string;
  showInstructionFields: boolean;
  showAmountField: boolean;
  mergeDocsSelected: {
    procuration: boolean;
    instruction_institution: boolean;
    photo: boolean;
    photo2: boolean;
  };
  isMerging: boolean;
  instructionLemaniaRef: React.RefObject<HTMLInputElement | null>;
  instructionBanqueRef: React.RefObject<HTMLInputElement | null>;
  decompteRef: React.RefObject<HTMLInputElement | null>;
  getAvailableStatuses: (currentStatus: string) => string[];
  handleStatusChange: (status: string) => void;
  handleConfirmStatus: (e: React.FormEvent) => void;
  handleMergeDocuments: () => void;
  setMergeDocsSelected: React.Dispatch<React.SetStateAction<{
    procuration: boolean;
    instruction_institution: boolean;
    photo: boolean;
    photo2: boolean;
  }>>;
  updateStatusMutation: { isPending: boolean };
  validateMutation: { isPending: boolean; mutate: () => void };
  uebertragResult: { success: boolean; message: string; screenshot?: string | null; screenshots?: Record<string, string> | null; demande_transfert?: string } | null;
  uebertragIsSubmitting: boolean;
  handleUebertragSubmit: (fondation?: string, reference?: string) => void;
  handleStatusChange2: (status: string) => void;
  getFileUrl: (path: string | null | undefined) => string;
  onRefetch?: () => void;
}

const PensionFundStatusForm: React.FC<PensionFundStatusFormProps> = ({
  pensionFund,
  statusColors,
  selectedStatus,
  showInstructionFields,
  showAmountField,
  mergeDocsSelected,
  isMerging,
  instructionLemaniaRef,
  instructionBanqueRef,
  decompteRef,
  getAvailableStatuses,
  handleStatusChange,
  handleConfirmStatus,
  handleMergeDocuments,
  setMergeDocsSelected,
  updateStatusMutation,
  validateMutation,
  uebertragResult,
  uebertragIsSubmitting,
  handleUebertragSubmit,
  handleStatusChange2,
  getFileUrl,
  onRefetch,
}) => {
  const notify = useNotification();
  const [isGeneratingCaisseInstruction, setIsGeneratingCaisseInstruction] = useState(false);
  const [isGeneratingFondationInstruction, setIsGeneratingFondationInstruction] = useState(false);
  const [isGeneratingDemandeTransfert, setIsGeneratingDemandeTransfert] = useState(false);
  const [isResetting, setIsResetting] = useState<'caisse' | 'fondation' | 'demande_transfert' | null>(null);

  // Supported fondations for auto-fill
  const supportedFondations = ['lemania', 'opsion'];
  const canGenerateFondationInstruction = (fondationName: string) =>
    supportedFondations.some(f => fondationName.toLowerCase().includes(f));

  // Track file selection state for manual uploads
  const [fondationFileSelected, setFondationFileSelected] = useState(false);
  const [caisseFileSelected, setCaisseFileSelected] = useState(false);

  // Reference field state (for Lemania in suppletive flow)
  const [referenceValue, setReferenceValue] = useState(pensionFund?.reference || '');

  const handleGenerateCaisseInstruction = async () => {
    if (!pensionFund?.uuid) return;

    // Use selected fondation (regroupement) or pension fund's fondation
    const effectiveFondation = selectedFondation || pensionFund.fondation;

    setIsGeneratingCaisseInstruction(true);
    try {
      const response = await pensionFundsApi.generateCaisseInstruction(pensionFund.uuid, effectiveFondation);
      notify.success(response.data.message);
      onRefetch?.();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notify.error(err.response?.data?.message || 'Erreur lors de la génération');
    } finally {
      setIsGeneratingCaisseInstruction(false);
    }
  };

  const handleGenerateFondationInstruction = async (fondationName: string) => {
    if (!pensionFund?.uuid) return;

    setIsGeneratingFondationInstruction(true);
    try {
      const response = await pensionFundsApi.generateFondationInstruction(pensionFund.uuid, fondationName);
      notify.success(response.data.message);
      onRefetch?.();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notify.error(err.response?.data?.message || 'Erreur lors de la génération');
    } finally {
      setIsGeneratingFondationInstruction(false);
    }
  };

  const handleGenerateDemandeTransfert = async () => {
    if (!pensionFund?.uuid) return;

    setIsGeneratingDemandeTransfert(true);
    try {
      const response = await pensionFundsApi.generateDemandeTransfert(pensionFund.uuid);
      notify.success(response.data.message);
      onRefetch?.();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notify.error(err.response?.data?.message || 'Erreur lors de la génération');
    } finally {
      setIsGeneratingDemandeTransfert(false);
    }
  };

  const handleResetInstruction = async (type: 'caisse' | 'fondation' | 'demande_transfert') => {
    if (!pensionFund?.uuid) return;

    const typeLabel = type === 'caisse' ? 'de la caisse' : type === 'fondation' ? 'de la fondation' : 'de la demande de transfert';
    if (!window.confirm(`Êtes-vous sûr de vouloir réinitialiser l'instruction ${typeLabel} ? Le document généré sera supprimé.`)) {
      return;
    }

    setIsResetting(type);
    try {
      const response = await pensionFundsApi.resetGeneratedInstruction(pensionFund.uuid, type);
      notify.success(response.data.message);
      onRefetch?.();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notify.error(err.response?.data?.message || 'Erreur lors de la réinitialisation');
    } finally {
      setIsResetting(null);
    }
  };

  const docs = pensionFund?.customer?.regroupement ? pensionFund.customer.contrat_regroupement_documents ?? [] : [];
  const [selectedFondation, setSelectedFondation] = useState(() => docs.length === 1 ? docs[0].fondation_name : '');

  // Handle fondation change - clear file selection state and reset generated documents
  const handleFondationChange = async (fondationName: string) => {
    setSelectedFondation(fondationName);
    setFondationFileSelected(false);
    setCaisseFileSelected(false);

    // Clear file inputs
    if (instructionLemaniaRef.current) {
      instructionLemaniaRef.current.value = '';
    }
    if (instructionBanqueRef.current) {
      instructionBanqueRef.current.value = '';
    }

    // Reset both generated instruction documents when fondation changes
    if (pensionFund?.uuid) {
      try {
        // Reset fondation instruction if exists
        if (pensionFund.lemania_instruction_document) {
          await pensionFundsApi.resetGeneratedInstruction(pensionFund.uuid, 'fondation');
        }
        // Reset caisse instruction if exists
        if (pensionFund.instruction_document) {
          await pensionFundsApi.resetGeneratedInstruction(pensionFund.uuid, 'caisse');
        }
        // Refetch to update UI
        onRefetch?.();
      } catch (error) {
        console.error('Error resetting instructions:', error);
      }
    }
  };

  // Determine the effective fondation: auto if single contrat, otherwise from selection
  const effectiveFondation = useMemo(() => {
    if (!docs.length) return '';
    if (docs.length === 1) return docs[0].fondation_name;
    return selectedFondation;
  }, [docs, selectedFondation]);

  const showReferenceField = effectiveFondation.toLowerCase().includes('lemania');

  // Find the instruction de transfert document matching the selected fondation
  const instructionDoc = useMemo(() => {
    if (!effectiveFondation || !pensionFund?.customer?.instruction_documents?.length) return null;
    return pensionFund.customer.instruction_documents.find(
      (doc) => doc.fondation_name === effectiveFondation
    ) || null;
  }, [effectiveFondation, pensionFund?.customer?.instruction_documents]);

  // Check if the generated fondation instruction matches the selected fondation
  // The filename includes the fondation name (e.g., Signed_Instruction_lemania_...)
  const fondationInstructionMatchesSelection = useMemo(() => {
    if (!pensionFund?.lemania_instruction_document || !effectiveFondation) return false;
    const docPath = pensionFund.lemania_instruction_document.toLowerCase();
    const fondationLower = effectiveFondation.toLowerCase();
    // Check if the document path contains the fondation name
    return docPath.includes(`instruction_${fondationLower}`) ||
           docPath.includes(`_${fondationLower}_`);
  }, [pensionFund?.lemania_instruction_document, effectiveFondation]);

  // Check if documents are ready for "En cours" status (non-suppletive only)
  const areDocumentsReadyForEnCours = useMemo(() => {
    // For suppletive (caisse_id = 2), documents are handled differently
    if (pensionFund?.caisse_id === 2) return true;

    // Check demande de transfert document - required for all non-suppletive
    const demandeTransfertReady = !!pensionFund?.demande_transfert_document;

    // Check fondation instruction document - must match the selected fondation
    const fondationDocReady = fondationInstructionMatchesSelection || fondationFileSelected;

    // Check caisse instruction document (only if caisse requires it)
    const caisseRequiresDoc = pensionFund?.caisse?.formulaire_instruction && pensionFund?.caisse?.formulaire_instruction_pdf;
    const hasCaisseDoc = !!pensionFund?.instruction_document;
    const caisseDocReady = !caisseRequiresDoc || hasCaisseDoc || caisseFileSelected;

    return demandeTransfertReady && fondationDocReady && caisseDocReady;
  }, [pensionFund, fondationFileSelected, caisseFileSelected, fondationInstructionMatchesSelection]);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {pensionFund.status === 'Terminé' || pensionFund.status === 'Annulé' ? (
        /* Locked message for Terminé and Annulé statuses */
        <div className={`bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden`}>
          <div className={`bg-gradient-to-r ${
            pensionFund.status === 'Terminé'
              ? 'from-green-600 to-emerald-600'
              : 'from-orange-500 to-orange-600'
          } px-6 py-4`}>
            <h3 className="text-lg font-semibold text-white">
              {pensionFund.status === 'Terminé' ? 'Dossier terminé' : 'Dossier annulé'}
            </h3>
            <p className="text-xs text-white/80 mt-1">Statut final atteint</p>
          </div>
          <div className="p-6">
            <div className="text-center py-8">
              <div className={`inline-flex items-center justify-center w-16 h-16 ${
                pensionFund.status === 'Terminé'
                  ? 'bg-green-100'
                  : 'bg-orange-100'
              } rounded-full mb-4`}>
                <Check className={`h-8 w-8 ${
                  pensionFund.status === 'Terminé'
                    ? 'text-green-600'
                    : 'text-orange-600'
                }`} />
              </div>
              <p className="text-gray-600 mb-4">
                {pensionFund.status === 'Terminé'
                  ? 'Ce dossier est marqué comme terminé et ne peut plus être modifié.'
                  : 'Ce dossier a été annulé et ne peut plus être modifié.'}
              </p>
              <div className={`inline-flex items-center px-4 py-2 ${
                pensionFund.status === 'Terminé'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-orange-100 text-orange-800'
              } rounded-lg text-sm font-medium`}>
                Statut final: {pensionFund.status}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Status-specific action card */
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          <div className={`bg-gradient-to-r ${statusColors.bg} px-6 py-4`}>
            <h3 className="text-lg font-semibold text-white">Statut: {pensionFund.status}</h3>
            <p className="text-xs text-white/80 mt-1">Actions disponibles pour ce statut</p>
          </div>
          <div className="p-6">
            {/* Task list based on status */}
            <div className={`bg-gradient-to-br ${
              pensionFund.status === 'Nouveau' ? 'from-gray-50 to-gray-100 border-gray-200' :
              pensionFund.status === 'En cours' ? 'from-blue-50 to-indigo-50 border-blue-200' :
              pensionFund.status === 'Rejeté' ? 'from-red-50 to-red-100 border-red-200' :
              'from-green-50 to-emerald-50 border-green-200'
            } border rounded-lg p-5 mb-6`}>
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                <CheckCircle className="w-4 h-4 mr-2" />
                Tâches à effectuer
              </h4>
              <ul className="space-y-2.5">
                {pensionFund.status === 'Nouveau' && (
                  <>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="inline-block w-1.5 h-1.5 bg-gray-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                      <span>Vérifier les informations du dossier</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="inline-block w-1.5 h-1.5 bg-gray-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                      <span>Préparer les instructions institution et banque</span>
                    </li>
                    <li className="flex items-start text-sm text-gray-700">
                      <span className="inline-block w-1.5 h-1.5 bg-gray-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                      <span>Passer le dossier "En cours"</span>
                    </li>
                  </>
                )}

                {pensionFund.status === 'En cours' && (
                  <>
                    <li className="flex items-start text-sm text-blue-900">
                      <span className="inline-block w-1.5 h-1.5 bg-blue-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                      <span>Attendre la réponse de l'institution</span>
                    </li>
                    <li className="flex items-start text-sm text-blue-900">
                      <span className="inline-block w-1.5 h-1.5 bg-blue-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                      <span>Renseigner le montant final et date de décompte</span>
                    </li>
                    <li className="flex items-start text-sm text-blue-900">
                      <span className="inline-block w-1.5 h-1.5 bg-blue-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                      <span>Uploader le décompte de sortie</span>
                    </li>
                  </>
                )}
                {pensionFund.status === 'Rejeté' && (
                  <>
                    <li className="flex items-start text-sm text-red-900">
                      <span className="inline-block w-1.5 h-1.5 bg-red-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                      <span>Analyser la raison du rejet</span>
                    </li>
                    <li className="flex items-start text-sm text-red-900">
                      <span className="inline-block w-1.5 h-1.5 bg-red-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                      <span>Corriger les informations si nécessaire</span>
                    </li>
                    <li className="flex items-start text-sm text-red-900">
                      <span className="inline-block w-1.5 h-1.5 bg-red-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                      <span>Resoumettre le dossier</span>
                    </li>
                  </>
                )}
                {pensionFund.status === 'Transféré' && (
                  <>
                    <li className="flex items-start text-sm text-green-900">
                      <span className="inline-block w-1.5 h-1.5 bg-green-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                      <span>Vérifier que le transfert est bien reçu</span>
                    </li>
                    <li className="flex items-start text-sm text-green-900">
                      <span className="inline-block w-1.5 h-1.5 bg-green-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                      <span>Renseigner le montant de la commission</span>
                    </li>
                    <li className="flex items-start text-sm text-green-900">
                      <span className="inline-block w-1.5 h-1.5 bg-green-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                      <span>Clôturer le dossier</span>
                    </li>
                  </>
                )}
              </ul>
            </div>

            {/* Quick Actions for Rejeté */}
            {pensionFund.status === 'Rejeté' && (
              <button
                onClick={() => validateMutation.mutate()}
                disabled={validateMutation.isPending}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3.5 rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 flex items-center justify-center shadow-md hover:shadow-lg transition-all font-medium"
              >
                {validateMutation.isPending ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Validation en cours...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Valider le transfert
                  </>
                )}
              </button>
            )}

            {/* Status Change Form */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Changer le statut</h4>
              <form onSubmit={handleConfirmStatus} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Nouveau statut</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {getAvailableStatuses(pensionFund.status).map((status) => {
                      const statusStyle = status === 'En cours' ? { border: 'border-blue-500', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500', borderIdle: 'border-blue-200' }
                        : status === 'Transféré' ? { border: 'border-green-500', bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500', borderIdle: 'border-green-200' }
                        : status === 'Terminé' ? { border: 'border-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', borderIdle: 'border-emerald-200' }
                        : status === 'Annulé' ? { border: 'border-orange-500', bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500', borderIdle: 'border-orange-200' }
                        : status === 'Rejeté' ? { border: 'border-red-500', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500', borderIdle: 'border-red-200' }
                        : { border: 'border-primary-500', bg: 'bg-primary-50', text: 'text-primary-700', dot: 'bg-primary-500', borderIdle: 'border-gray-200' };
                      const isSelected = selectedStatus === status;
                      return (
                        <button
                          key={status}
                          type="button"
                          onClick={() => handleStatusChange(status)}
                          className={`flex items-center p-3 rounded-lg border-2 transition-all text-left ${
                            isSelected
                              ? `${statusStyle.border} ${statusStyle.bg} shadow-sm`
                              : `${statusStyle.borderIdle} bg-white hover:${statusStyle.bg}`
                          }`}
                        >
                          <div className={`w-4 h-4 rounded-full border-2 mr-3 flex-shrink-0 flex items-center justify-center ${
                            isSelected ? statusStyle.border : 'border-gray-300'
                          }`}>
                            {isSelected && (
                              <div className={`w-2 h-2 rounded-full ${statusStyle.dot}`} />
                            )}
                          </div>
                          <span className={`text-sm font-medium ${isSelected ? statusStyle.text : 'text-gray-700'}`}>
                            {status}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {showInstructionFields && (
                <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  {/* Fondation for regroupement contracts */}
                  {docs.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Fondation de regroupement {docs.length > 1 && '*'}
                      </label>
                      <input type="hidden" name="regroupement_fondation" value={effectiveFondation} />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {docs.map((doc) => {
                          const isSelected = effectiveFondation === doc.fondation_name;
                          const isSingle = docs.length === 1;
                          return (
                            <button
                              key={doc.id}
                              type="button"
                              onClick={() => !isSingle && handleFondationChange(doc.fondation_name)}
                              className={`relative flex items-center p-4 rounded-lg border-2 transition-all text-left ${
                                isSelected
                                  ? 'border-primary-500 bg-primary-50 shadow-sm'
                                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                              } ${isSingle ? 'cursor-default' : ''}`}
                            >
                              <div className={`w-5 h-5 rounded-full border-2 mr-3 flex-shrink-0 flex items-center justify-center ${
                                isSelected ? 'border-primary-500' : 'border-gray-300'
                              }`}>
                                {isSelected && (
                                  <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />
                                )}
                              </div>
                              <span className={`text-sm font-medium ${isSelected ? 'text-primary-700' : 'text-gray-700'}`}>
                                {doc.fondation_name}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* NON-SUPPLETIVE FLOW */}
                  {pensionFund?.caisse_id !== 2 && (
                    <div className="space-y-4">
                      {/* Step indicator */}
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold mr-2">1</span>
                        Documents à remplir
                      </div>

                      {/* Demande de transfert document */}
                      <div className="bg-white rounded-lg p-4 border border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-700">
                            Demande de transfert
                          </label>
                          <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">Requis</span>
                        </div>
                        <div className="space-y-2">
                          {pensionFund?.demande_transfert_document ? (
                            <>
                              <a
                                href={getFileUrl(pensionFund.demande_transfert_document)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center p-3 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 hover:border-emerald-300 transition-all group"
                              >
                                <CheckCircle className="w-5 h-5 text-emerald-600 mr-3" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-emerald-900">Demande de transfert générée</p>
                                  <p className="text-xs text-emerald-600">Cliquer pour voir le document</p>
                                </div>
                                <Eye className="w-4 h-4 text-emerald-400" />
                              </a>
                              <button
                                type="button"
                                onClick={() => handleResetInstruction('demande_transfert')}
                                disabled={isResetting === 'demande_transfert'}
                                className="w-full flex items-center justify-center p-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all text-sm text-gray-600 disabled:opacity-50"
                              >
                                {isResetting === 'demande_transfert' ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <RotateCcw className="w-4 h-4 mr-2" />
                                )}
                                Réinitialiser et regénérer
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={handleGenerateDemandeTransfert}
                              disabled={isGeneratingDemandeTransfert}
                              className="w-full flex items-center p-3 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 hover:border-green-300 transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isGeneratingDemandeTransfert ? (
                                <Loader2 className="w-5 h-5 text-green-600 mr-3 animate-spin" />
                              ) : (
                                <FileSignature className="w-5 h-5 text-green-600 mr-3 group-hover:scale-110 transition-transform" />
                              )}
                              <div className="flex-1 text-left">
                                <p className="text-sm font-medium text-green-900">
                                  {isGeneratingDemandeTransfert ? 'Génération en cours...' : 'Générer la demande de transfert'}
                                </p>
                                <p className="text-xs text-green-600">Auto-remplir avec les données client</p>
                              </div>
                              <ArrowRight className="w-4 h-4 text-green-400" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Instruction de transfert document */}
                      {instructionDoc && (
                        <div className="bg-white rounded-lg p-4 border border-blue-200">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700">
                              Instruction de transfert ({instructionDoc.fondation_name})
                            </label>
                            <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">Fondation</span>
                          </div>
                          <div className="space-y-2">
                            {/* Show generated document if exists */}
                            {fondationInstructionMatchesSelection ? (
                              <>
                                <a
                                  href={getFileUrl(pensionFund.lemania_instruction_document)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center p-3 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 hover:border-emerald-300 transition-all group"
                                >
                                  <CheckCircle className="w-5 h-5 text-emerald-600 mr-3" />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-emerald-900">Instruction remplie prête</p>
                                    <p className="text-xs text-emerald-600">Cliquer pour voir le document</p>
                                  </div>
                                  <Eye className="w-4 h-4 text-emerald-400" />
                                </a>
                                <button
                                  type="button"
                                  onClick={() => handleResetInstruction('fondation')}
                                  disabled={isResetting === 'fondation'}
                                  className="w-full flex items-center justify-center p-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all text-sm text-gray-600 disabled:opacity-50"
                                >
                                  {isResetting === 'fondation' ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <RotateCcw className="w-4 h-4 mr-2" />
                                  )}
                                  Réinitialiser et regénérer
                                </button>
                              </>
                            ) : (
                              <>
                                {/* Download blank template */}
                                <a
                                  href={getFileUrl(instructionDoc.file_path)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all group"
                                >
                                  <Download className="w-5 h-5 text-gray-600 mr-3 group-hover:scale-110 transition-transform" />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">Télécharger le modèle vierge</p>
                                    <p className="text-xs text-gray-500">Pour remplir manuellement</p>
                                  </div>
                                </a>

                                {/* Generate button if fondation supports it */}
                                {canGenerateFondationInstruction(instructionDoc.fondation_name) ? (
                                  <button
                                    type="button"
                                    onClick={() => handleGenerateFondationInstruction(instructionDoc.fondation_name)}
                                    disabled={isGeneratingFondationInstruction}
                                    className="w-full flex items-center p-3 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 hover:border-green-300 transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {isGeneratingFondationInstruction ? (
                                      <Loader2 className="w-5 h-5 text-green-600 mr-3 animate-spin" />
                                    ) : (
                                      <FileSignature className="w-5 h-5 text-green-600 mr-3 group-hover:scale-110 transition-transform" />
                                    )}
                                    <div className="flex-1 text-left">
                                      <p className="text-sm font-medium text-green-900">
                                        {isGeneratingFondationInstruction ? 'Génération en cours...' : 'Générer automatiquement'}
                                      </p>
                                      <p className="text-xs text-green-600">Auto-remplir avec les données client</p>
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-green-400" />
                                  </button>
                                ) : (
                                  /* Manual upload for fondations without auto-generator */
                                  <div className="mt-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Téléverser le document signé *
                                    </label>
                                    <input
                                      type="file"
                                      ref={instructionLemaniaRef}
                                      accept=".pdf"
                                      onChange={(e) => setFondationFileSelected(!!e.target.files?.length)}
                                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                                    />
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Caisse formulaire d'instruction */}
                      {pensionFund?.caisse?.formulaire_instruction && pensionFund.caisse.formulaire_instruction_pdf && (
                        <div className="bg-white rounded-lg p-4 border border-blue-200">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700">
                              Formulaire d'instruction ({pensionFund.caisse.caisse})
                            </label>
                            <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700">Caisse</span>
                          </div>
                          <div className="space-y-2">
                            {/* Show status: generated or not */}
                            {pensionFund.instruction_document ? (
                              <>
                                <a
                                  href={getFileUrl(pensionFund.instruction_document)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center p-3 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 hover:border-emerald-300 transition-all group"
                                >
                                  <CheckCircle className="w-5 h-5 text-emerald-600 mr-3" />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-emerald-900">Instruction remplie prête</p>
                                    <p className="text-xs text-emerald-600">Cliquer pour voir le document</p>
                                  </div>
                                  <Eye className="w-4 h-4 text-emerald-400" />
                                </a>
                                <button
                                  type="button"
                                  onClick={() => handleResetInstruction('caisse')}
                                  disabled={isResetting === 'caisse'}
                                  className="w-full flex items-center justify-center p-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all text-sm text-gray-600 disabled:opacity-50"
                                >
                                  {isResetting === 'caisse' ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <RotateCcw className="w-4 h-4 mr-2" />
                                  )}
                                  Réinitialiser et regénérer
                                </button>
                              </>
                            ) : (
                              <>
                                <a
                                  href={getFileUrl(pensionFund.caisse.formulaire_instruction_pdf)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all group"
                                >
                                  <Download className="w-5 h-5 text-gray-600 mr-3 group-hover:scale-110 transition-transform" />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">Télécharger le formulaire vierge</p>
                                    <p className="text-xs text-gray-500">Pour remplir manuellement</p>
                                  </div>
                                </a>

                                {/* Generate signed instruction button or manual upload */}
                                {pensionFund.caisse.instruction_generator_class ? (
                                  <button
                                    type="button"
                                    onClick={handleGenerateCaisseInstruction}
                                    disabled={isGeneratingCaisseInstruction}
                                    className="w-full flex items-center p-3 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 hover:border-green-300 transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {isGeneratingCaisseInstruction ? (
                                      <Loader2 className="w-5 h-5 text-green-600 mr-3 animate-spin" />
                                    ) : (
                                      <FileSignature className="w-5 h-5 text-green-600 mr-3 group-hover:scale-110 transition-transform" />
                                    )}
                                    <div className="flex-1 text-left">
                                      <p className="text-sm font-medium text-green-900">
                                        {isGeneratingCaisseInstruction ? 'Génération en cours...' : 'Générer automatiquement'}
                                      </p>
                                      <p className="text-xs text-green-600">Auto-remplir avec les données client</p>
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-green-400" />
                                  </button>
                                ) : (
                                  /* Manual upload for caisses without auto-generator */
                                  <div className="mt-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Téléverser le document signé *
                                    </label>
                                    <input
                                      type="file"
                                      ref={instructionBanqueRef}
                                      accept=".pdf"
                                      onChange={(e) => setCaisseFileSelected(!!e.target.files?.length)}
                                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                                    />
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}

                    </div>
                  )}

                  {/* SUPPLETIVE FLOW */}
                  {/* Reference field - only for suppletive (caisse_id = 2) */}
                  {pensionFund?.caisse_id === 2 && showReferenceField && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Référence
                      </label>
                      <input
                        type="text"
                        name="reference"
                        value={referenceValue}
                        onChange={(e) => setReferenceValue(e.target.value)}
                        placeholder="Référence"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white"
                      />
                    </div>
                  )}

                  {/* Institution supplétive section (caisse_id = 2) */}
                  {pensionFund?.customer && pensionFund?.caisse_id === 2 && (
                    <div className="space-y-4">
                      {/* Step 1: Instruction de transfert document */}
                      {instructionDoc && (
                        <div className="bg-white rounded-lg p-4 border border-blue-200">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700">
                              Instruction de transfert ({instructionDoc.fondation_name})
                            </label>
                            <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">Étape 1</span>
                          </div>
                          <div className="space-y-2">
                            {/* Show generated document if exists */}
                            {fondationInstructionMatchesSelection ? (
                              <>
                                <a
                                  href={getFileUrl(pensionFund.lemania_instruction_document)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center p-3 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 hover:border-emerald-300 transition-all group"
                                >
                                  <CheckCircle className="w-5 h-5 text-emerald-600 mr-3" />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-emerald-900">Instruction remplie prête</p>
                                    <p className="text-xs text-emerald-600">Cliquer pour voir le document</p>
                                  </div>
                                  <Eye className="w-4 h-4 text-emerald-400" />
                                </a>
                                <button
                                  type="button"
                                  onClick={() => handleResetInstruction('fondation')}
                                  disabled={isResetting === 'fondation'}
                                  className="w-full flex items-center justify-center p-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all text-sm text-gray-600 disabled:opacity-50"
                                >
                                  {isResetting === 'fondation' ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <RotateCcw className="w-4 h-4 mr-2" />
                                  )}
                                  Réinitialiser et regénérer
                                </button>
                              </>
                            ) : (
                              <>
                                {/* Download blank template */}
                                <a
                                  href={getFileUrl(instructionDoc.file_path)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all group"
                                >
                                  <Download className="w-5 h-5 text-gray-600 mr-3 group-hover:scale-110 transition-transform" />
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">Télécharger le modèle vierge</p>
                                    <p className="text-xs text-gray-500">Pour remplir manuellement</p>
                                  </div>
                                </a>

                                {/* Generate button if fondation supports it */}
                                {canGenerateFondationInstruction(instructionDoc.fondation_name) ? (
                                  <button
                                    type="button"
                                    onClick={() => handleGenerateFondationInstruction(instructionDoc.fondation_name)}
                                    disabled={isGeneratingFondationInstruction}
                                    className="w-full flex items-center p-3 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 hover:border-green-300 transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {isGeneratingFondationInstruction ? (
                                      <Loader2 className="w-5 h-5 text-green-600 mr-3 animate-spin" />
                                    ) : (
                                      <FileSignature className="w-5 h-5 text-green-600 mr-3 group-hover:scale-110 transition-transform" />
                                    )}
                                    <div className="flex-1 text-left">
                                      <p className="text-sm font-medium text-green-900">
                                        {isGeneratingFondationInstruction ? 'Génération en cours...' : 'Générer automatiquement'}
                                      </p>
                                      <p className="text-xs text-green-600">Auto-remplir avec les données client</p>
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-green-400" />
                                  </button>
                                ) : (
                                  /* Manual upload for fondations without auto-generator */
                                  <div className="mt-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Téléverser le document signé *
                                    </label>
                                    <input
                                      type="file"
                                      ref={instructionLemaniaRef}
                                      accept=".pdf"
                                      onChange={(e) => setFondationFileSelected(!!e.target.files?.length)}
                                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                                    />
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Step 2: AEIS Uebertrag Submission */}
                      <div className="pt-4 border-t border-blue-200 space-y-3">
                        <h4 className="text-sm font-semibold text-gray-900 flex items-center">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Formulaire Institution supplétive (AEIS)
                          <span className="ml-2 text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">Étape 2</span>
                        </h4>

                        {/* Warning if instruction not ready */}
                        {instructionDoc && !fondationInstructionMatchesSelection && !fondationFileSelected && (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-sm text-amber-800 flex items-center">
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              Veuillez d'abord générer l'instruction de transfert (Étape 1)
                            </p>
                          </div>
                        )}

                        {/* Uebertrag Result Display */}
                        {uebertragResult && (
                          <div className="space-y-3">
                            <div className={`p-4 rounded-lg border ${uebertragResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                              <p className={`text-sm font-medium flex items-center ${uebertragResult.success ? 'text-green-700' : 'text-red-700'}`}>
                                {uebertragResult.success ? <CheckCircle className="w-4 h-4 mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                                {uebertragResult.message}
                              </p>
                            </div>

                            {/* Demande transfert document card */}
                            {uebertragResult.success && uebertragResult.demande_transfert && (
                              <a
                                href={getFileUrl(uebertragResult.demande_transfert)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group flex items-center justify-between bg-white border-2 border-green-300 rounded-lg p-4 hover:border-green-500 hover:shadow-md transition-all"
                              >
                                <div className="flex items-center">
                                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-green-200 transition-colors">
                                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                  </div>
                                  <div>
                                    <p className="text-sm font-semibold text-gray-900 group-hover:text-green-700 transition-colors">Consulter la demande de transfert</p>
                                    <p className="text-xs text-gray-600">Cliquez pour ouvrir dans un nouvel onglet</p>
                                  </div>
                                </div>
                                <Eye className="w-5 h-5 text-green-600 group-hover:scale-110 transition-transform" />
                              </a>
                            )}

                            {/* Step screenshots */}
                            {uebertragResult.screenshots && Object.entries(uebertragResult.screenshots).map(([step, base64]) => {
                              const stepLabels: Record<string, string> = {
                                step1: 'Étape 1 — Données personnelles',
                                step2: 'Étape 2 — Employeur',
                                step3: 'Étape 3 — Caisse de pension',
                                step4: 'Étape 4 — Transmission',
                                confirmation: 'Confirmation de soumission',
                              };
                              return (
                                <a
                                  key={step}
                                  href={`data:image/png;base64,${base64}`}
                                  download={`uebertrag_${step}_${Date.now()}.png`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`group flex items-center justify-between bg-white border-2 ${uebertragResult.success ? 'border-green-300 hover:border-green-500' : 'border-red-300 hover:border-red-500'} rounded-lg p-4 hover:shadow-md transition-all`}
                                >
                                  <div className="flex items-center">
                                    <div className={`w-12 h-12 ${uebertragResult.success ? 'bg-green-100 group-hover:bg-green-200' : 'bg-red-100 group-hover:bg-red-200'} rounded-lg flex items-center justify-center mr-4 transition-colors`}>
                                      <svg className={`w-6 h-6 ${uebertragResult.success ? 'text-green-600' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                    </div>
                                    <div>
                                      <p className={`text-sm font-semibold text-gray-900 ${uebertragResult.success ? 'group-hover:text-green-700' : 'group-hover:text-red-700'} transition-colors`}>{stepLabels[step] || step}</p>
                                      <p className="text-xs text-gray-600">Cliquez pour télécharger</p>
                                    </div>
                                  </div>
                                  <Download className={`w-5 h-5 ${uebertragResult.success ? 'text-green-600' : 'text-red-600'} group-hover:scale-110 transition-transform`} />
                                </a>
                              );
                            })}

                            {/* Fallback: single screenshot if no step screenshots */}
                            {!uebertragResult.screenshots && uebertragResult.screenshot && (
                              <a
                                href={`data:image/png;base64,${uebertragResult.screenshot}`}
                                download={`uebertrag_screenshot_${Date.now()}.png`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`group flex items-center justify-between bg-white border-2 ${uebertragResult.success ? 'border-green-300 hover:border-green-500' : 'border-red-300 hover:border-red-500'} rounded-lg p-4 hover:shadow-md transition-all`}
                              >
                                <div className="flex items-center">
                                  <div className={`w-12 h-12 ${uebertragResult.success ? 'bg-green-100 group-hover:bg-green-200' : 'bg-red-100 group-hover:bg-red-200'} rounded-lg flex items-center justify-center mr-4 transition-colors`}>
                                    <svg className={`w-6 h-6 ${uebertragResult.success ? 'text-green-600' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                  </div>
                                  <div>
                                    <p className={`text-sm font-semibold text-gray-900 ${uebertragResult.success ? 'group-hover:text-green-700' : 'group-hover:text-red-700'} transition-colors`}>Capture d'écran du formulaire</p>
                                    <p className="text-xs text-gray-600">Cliquez pour télécharger</p>
                                  </div>
                                </div>
                                <Download className={`w-5 h-5 ${uebertragResult.success ? 'text-green-600' : 'text-red-600'} group-hover:scale-110 transition-transform`} />
                              </a>
                            )}
                          </div>
                        )}

                        {/* Loading State */}
                        {uebertragIsSubmitting && (
                          <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg text-center">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
                            <p className="text-sm font-medium text-blue-900">Soumission en cours...</p>
                            <p className="text-xs text-blue-600 mt-1">Le formulaire de transfert est en train d'être rempli. Veuillez patienter.</p>
                          </div>
                        )}

                        {/* Submit / Validate Button */}
                        {!uebertragIsSubmitting && !uebertragResult?.success && (
                          <button
                            type="button"
                            onClick={() => handleUebertragSubmit(effectiveFondation || undefined, referenceValue || undefined)}
                            disabled={uebertragIsSubmitting || !!(instructionDoc && !fondationInstructionMatchesSelection && !fondationFileSelected)}
                            className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-md hover:shadow-lg transition-all font-medium"
                          >
                            <ArrowRight className="w-5 h-5 mr-2" />
                            Soumettre à l'institution supplétive
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

                {/* Fields for En cours -> Transféré transition */}
                {selectedStatus === 'Transféré' && (
                  <div className="space-y-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Montant final (CHF) *</label>
                      <input
                        type="number"
                        name="amount"
                        step="0.01"
                        min="0"
                        defaultValue={pensionFund?.amount || ''}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date du décompte de sortie *</label>
                      <input
                        type="date"
                        name="exit_statement_date"
                        defaultValue={pensionFund?.exit_statement_date || ''}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Décompte de sortie *
                        {pensionFund?.exit_statement_document && <span className="text-green-600 ml-2">✓ Déjà uploadé</span>}
                      </label>
                      <input
                        type="file"
                        ref={decompteRef}
                        accept=".pdf"
                        className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                        required={!pensionFund?.exit_statement_document}
                      />
                    </div>
                  </div>
                )}

                {/* Fields for Transféré -> Terminé transition (Commission) */}
                <PensionFundCommission
                  pensionFund={pensionFund}
                  selectedStatus={selectedStatus}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Raison / Commentaire {(selectedStatus === 'Annulé' || selectedStatus === 'Rejeté') && '*'}
                  </label>
                  <textarea
                    name="reason"
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder={
                      selectedStatus === 'Annulé' || selectedStatus === 'Rejeté'
                        ? 'Expliquez la raison du changement de statut... (requis)'
                        : 'Optionnel: Ajoutez un commentaire...'
                    }
                    required={selectedStatus === 'Annulé' || selectedStatus === 'Rejeté'}
                  />
                </div>

                  <button
                    type="submit"
                    disabled={
                      !selectedStatus ||
                      updateStatusMutation.isPending ||
                      (selectedStatus === 'En cours' && pensionFund?.caisse_id === 2 && !uebertragResult?.success) ||
                      (selectedStatus === 'En cours' && pensionFund?.caisse_id !== 2 && !areDocumentsReadyForEnCours)
                    }
                    className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 ${uebertragResult?.success && selectedStatus === 'En cours' ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700' : 'bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800'} text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg`}
                  >
                    {updateStatusMutation.isPending ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Mise à jour...
                      </>
                    ) : (
                      <>
                        <Check className="h-5 w-5" />
                        Valider
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default PensionFundStatusForm;
